import * as Sentry from '@sentry/react';
import type { feedbackIntegration } from '@sentry/react';
import { getSession } from '@/auth/AuthSession';
import { NAVBAR_HEIGHT } from '@/constants/layout';

type SentryFeedbackOptions = NonNullable<Parameters<typeof feedbackIntegration>[0]>;

type Translate = (key: string) => string;

export function createSentryFeedbackOptions(translate: Translate): SentryFeedbackOptions {
	return {
		id: 'sentry-feedback',
		autoInject: false,
		showBranding: true,
		showEmail: false,
		showName: false,
		isEmailRequired: false,
		isNameRequired: false,
		enableScreenshot: true,
		useSentryUser: {
			email: 'email',
			name: 'name',
		},
		tags: {
			source: 'in-app-widget',
		},
		// Read once: the widget can't switch schemes, so a change shows on the next launch.
		colorScheme: document.documentElement.getAttribute('data-mantine-color-scheme') === 'dark' ? 'dark' : 'light',
		themeLight: {
			accentBackground: '#21a37b',
			accentForeground: '#ffffff',
			background: '#ffffff',
			foreground: '#1f2937',
			successColor: '#21a37b',
			errorColor: '#fa5252',
			boxShadow: '0 16px 40px rgba(15, 23, 42, 0.18)',
			outline: '2px solid rgba(33, 163, 123, 0.35)',
		},
		themeDark: {
			accentBackground: '#21a37b',
			accentForeground: '#ffffff',
			background: '#182028',
			foreground: '#f8fafc',
			successColor: '#3ecf8e',
			errorColor: '#ff8787',
			boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
			outline: '2px solid rgba(33, 163, 123, 0.45)',
		},
		triggerLabel: translate('feedback.triggerLabel'),
		triggerAriaLabel: translate('feedback.triggerAriaLabel'),
		cancelButtonLabel: translate('feedback.cancelButtonLabel'),
		submitButtonLabel: translate('feedback.submitButtonLabel'),
		confirmButtonLabel: translate('feedback.confirmButtonLabel'),
		formTitle: translate('feedback.formTitle'),
		emailLabel: translate('feedback.emailLabel'),
		emailPlaceholder: translate('feedback.emailPlaceholder'),
		messageLabel: translate('feedback.messageLabel'),
		messagePlaceholder: translate('feedback.messagePlaceholder'),
		nameLabel: translate('feedback.nameLabel'),
		namePlaceholder: translate('feedback.namePlaceholder'),
		successMessageText: translate('feedback.successMessageText'),
		isRequiredLabel: translate('feedback.isRequiredLabel'),
		addScreenshotButtonLabel: translate('feedback.addScreenshotButtonLabel'),
		removeScreenshotButtonLabel: translate('feedback.removeScreenshotButtonLabel'),
		highlightToolText: translate('feedback.highlightToolText'),
		hideToolText: translate('feedback.hideToolText'),
		removeHighlightText: translate('feedback.removeHighlightText'),
	};
}

export async function openSentryFeedbackDialog(anchorElement?: HTMLElement): Promise<void> {
	const feedback = Sentry.getFeedback();
	if (!feedback) {
		return;
	}

	const session = getSession();
	const user = Sentry.getCurrentScope().getUser();
	const sessionEmail = session?.email?.trim();
	if (sessionEmail && user?.email !== sessionEmail) {
		Sentry.setUser({
			...user,
			email: sessionEmail,
		});
	}

	const dialog = await feedback.createForm();
	dialog.appendToDom();
	if (dialog.el instanceof HTMLElement) {
		positionSentryFeedbackDialog(dialog.el, anchorElement);
	}
	dialog.open();
}

function positionSentryFeedbackDialog(dialogElement: HTMLElement, anchorElement?: HTMLElement): void {
	const rootNode = dialogElement.getRootNode();
	if (!(rootNode instanceof ShadowRoot) || !(rootNode.host instanceof HTMLElement)) {
		return;
	}

	const host = rootNode.host;
	const fallbackTop = NAVBAR_HEIGHT;
	const fallbackRight = 16;

	if (!anchorElement) {
		host.style.setProperty('--dialog-inset', `${fallbackTop}px ${fallbackRight}px auto auto`);
		return;
	}

	const anchorRect = anchorElement.getBoundingClientRect();
	const top = Math.max(fallbackTop, Math.round(anchorRect.bottom));
	const right = 0;

	host.style.setProperty('--dialog-inset', `${top}px ${right}px auto auto`);
}
